import { useCallback, useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import { useBusinessStore } from '../store/useBusinessStore';
import { useAuthStore } from '../store/useAuthStore';
import { supabase } from '../supabaseClient';
import { normalizeTestNumber } from '../utils/botTestMode';
import { checkAnswer, checkKnowledge } from '../utils/botQuestions';

const DEMO_BUSINESS_IDS = ['9001', '9002', '9003'];

export const isMissingBotKnowledge = (error) =>
  ['PGRST202', '42883', '42P01', 'PGRST205', '42703', 'PGRST204'].includes(error?.code) || /bot_questions|bot_knowledge|bot_aprende|answer_bot_question/i.test(error?.message || '');

const explain = (error, fallback) => (error?.code === '42501' ? 'No tienes permiso sobre este negocio (solo el dueño o un administrador).' : fallback);

// Preguntas que el bot no supo contestar, lo que ya aprendió y sus ajustes. Las reglas viven en la base (20261015a_bot_knowledge.sql).
export const useBotQuestions = () => {
  const { activeBusinessId } = useBusinessStore();
  const { userRole } = useAuthStore();
  const isReadOnlyDemo = DEMO_BUSINESS_IDS.includes(String(activeBusinessId)) && userRole !== 'master';
  const [questions, setQuestions] = useState([]);
  const [knowledge, setKnowledge] = useState([]);
  const [learning, setLearningState] = useState(true);
  const [notifyPhone, setNotifyPhone] = useState('');
  const [loading, setLoading] = useState(true);
  const [missing, setMissing] = useState(false);

  const load = useCallback(async () => {
    if (!activeBusinessId) { setLoading(false); return; }
    setLoading(true);
    const business = String(activeBusinessId);
    const [q, k, b, s] = await Promise.all([
      supabase.from('bot_questions').select('id, number, customer_name, customer_number, customer_jid, question, status, answer, learn, answered_by, answered_at, notify_status, notify_error, delivery_status, delivery_error, created_at')
        .eq('business_id', business).order('created_at', { ascending: false }).limit(100),
      supabase.from('bot_knowledge').select('id, question, answer, active, source, updated_at')
        .eq('business_id', business).order('updated_at', { ascending: false }).limit(300),
      supabase.from('businesses').select('bot_aprende').eq('id', business).maybeSingle(),
      supabase.from('billing_settings').select('notify_phone').eq('business_id', business).maybeSingle()
    ]);
    const error = q.error || k.error || b.error;
    if (error) {
      const gone = isMissingBotKnowledge(error);
      setMissing(gone);
      if (!gone) toast.error(explain(error, 'No se pudieron leer las preguntas del bot.'));
      setQuestions([]); setKnowledge([]);
    } else {
      setMissing(false);
      setQuestions(q.data || []);
      setKnowledge(k.data || []);
      setLearningState(b.data?.bot_aprende !== false);
      setNotifyPhone(s.error ? '' : (s.data?.notify_phone || ''));
    }
    setLoading(false);
  }, [activeBusinessId]);

  useEffect(() => { load(); }, [load]);

  const guardDemo = () => {
    if (isReadOnlyDemo) { toast.error('Modo solo lectura: las cuentas demo no se pueden modificar.'); return true; }
    return false;
  };

  const answer = async (id, text, learn = true) => {
    if (guardDemo()) return false;
    const problem = checkAnswer(text);
    if (problem) { toast.error(problem); return false; }
    const { data, error } = await supabase.rpc('answer_bot_question', { p_id: id, p_answer: String(text).trim(), p_learn: Boolean(learn) });
    if (error) { toast.error(explain(error, 'No se pudo guardar la respuesta.')); return false; }
    if (data?.estado === 'ya_cerrada') toast.error('Esa pregunta ya estaba respondida o descartada.');
    else toast.success(data?.entrega ? 'Respondida: se la enviamos al cliente por WhatsApp.' : 'Respondida. Es de hace más de 3 días: no se le envía al cliente.');
    await load();
    return data?.estado === 'respondida';
  };

  const discard = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('discard_bot_question', { p_id: id });
    if (error) { toast.error(explain(error, 'No se pudo descartar la pregunta.')); return false; }
    toast.success('Pregunta descartada');
    await load();
    return true;
  };

  const addKnowledge = async (values) => {
    if (guardDemo()) return false;
    const problem = checkKnowledge(values);
    if (problem) { toast.error(problem); return false; }
    const { error } = await supabase.from('bot_knowledge').insert([{ business_id: String(activeBusinessId), question: String(values.question).trim(), answer: String(values.answer).trim(), active: true }]);
    if (error) { toast.error(explain(error, 'No se pudo guardar.')); return false; }
    toast.success('Guardado: el bot ya lo sabe');
    await load();
    return true;
  };

  const updateKnowledge = async (id, values) => {
    if (guardDemo()) return false;
    const patch = {};
    if ('question' in values || 'answer' in values) {
      const problem = checkKnowledge(values);
      if (problem) { toast.error(problem); return false; }
      patch.question = String(values.question).trim();
      patch.answer = String(values.answer).trim();
    }
    if ('active' in values) patch.active = Boolean(values.active);
    const { error } = await supabase.from('bot_knowledge').update(patch).eq('id', id).eq('business_id', String(activeBusinessId));
    if (error) { toast.error(explain(error, 'No se pudo guardar.')); return false; }
    await load();
    return true;
  };

  const removeKnowledge = async (id) => {
    if (guardDemo()) return false;
    const { error } = await supabase.from('bot_knowledge').delete().eq('id', id).eq('business_id', String(activeBusinessId));
    if (error) { toast.error(explain(error, 'No se pudo borrar.')); return false; }
    await load();
    return true;
  };

  const setLearning = async (on) => {
    if (guardDemo()) return false;
    const { error } = await supabase.rpc('set_bot_aprende', { p_business: String(activeBusinessId), p_on: Boolean(on) });
    if (error) { toast.error(explain(error, 'No se pudo cambiar el ajuste.')); return false; }
    setLearningState(Boolean(on));
    toast.success(on ? 'El bot consultará lo que no sepa' : 'El bot ya no consultará ni usará lo aprendido');
    return true;
  };

  const saveNotifyPhone = async (raw) => {
    if (guardDemo()) return false;
    const text = String(raw ?? '').trim();
    const phone = text ? normalizeTestNumber(text) : null;
    if (text && !phone) { toast.error('El número no es válido: escribe el celular con indicativo (ej. 573016867636).'); return false; }
    const { error } = await supabase.from('billing_settings').upsert([{ business_id: String(activeBusinessId), notify_phone: phone }], { onConflict: 'business_id' });
    if (error) { toast.error(explain(error, 'No se pudo guardar el número de avisos.')); return false; }
    setNotifyPhone(phone || '');
    toast.success(phone ? 'Número de avisos guardado' : 'Sin número de avisos: las preguntas quedan solo aquí');
    return true;
  };

  return { questions, knowledge, learning, notifyPhone, loading, missing, isReadOnlyDemo, reload: load, answer, discard, addKnowledge, updateKnowledge, removeKnowledge, setLearning, saveNotifyPhone };
};
